import { AsyncThunkPayloadCreator, createAsyncThunk } from '@reduxjs/toolkit';
import Stardog from 'stardog';

import { MimeType } from 'bones/constants/mimeTypes';
import { formatJsonLd } from 'bones/utils/jsonld/formatJsonLdResults';
import { requestsCopy } from 'bones/utils/request/copy';
import { isBody } from 'bones/utils/request/isBody';
import { handleSessionTimeoutRedirectResponse } from 'bones/utils/sessionTimeoutRedirect';

const connectionRefusedChecker = /CONNECTION_REFUSED$/;

interface StardogThunkCallbackArgs {
  connection: Stardog.Connection;
}
export interface StardogThunkApiConfig {
  state: any;
  rejectValue: { message: string; response?: Stardog.HTTP.Body };
}

interface HandlerProps<Connection> {
  err: any;
  connection: Connection;
  args: Parameters<
    AsyncThunkPayloadCreator<Stardog.HTTP.Body, unknown, StardogThunkApiConfig>
  >[0];
  api: Parameters<
    AsyncThunkPayloadCreator<Stardog.HTTP.Body, unknown, StardogThunkApiConfig>
  >[1];
}

export interface StardogAsyncThunkWrapperArgs<State, Connection> {
  connectionSelector: (state: State) => Connection | null;
  getStardogConnection: (connection: Connection) => Stardog.Connection;
  onConnectionRefused?: (props: HandlerProps<Connection>) => void;
  onConnectionUnauthorized?: (props: HandlerProps<Connection>) => void;
}

/**
 * Wrapper for making createStardogAsyncThunk in apps with local state dispatchers
 */
export const stardogAsyncThunkWrapper =
  <Connection, State>({
    connectionSelector,
    getStardogConnection,
    onConnectionRefused,
    onConnectionUnauthorized,
  }: StardogAsyncThunkWrapperArgs<State, Connection>) =>
  <Args>(
    typePrefix: string,
    callback: (
      args: Args & StardogThunkCallbackArgs
    ) => Promise<Stardog.HTTP.Body>
  ) =>
    createAsyncThunk<
      Stardog.HTTP.Body,
      Args & { connection?: Connection },
      StardogThunkApiConfig
    >(typePrefix, async (args, api) => {
      const { getState, rejectWithValue } = api;
      const connection = args.connection || connectionSelector(getState());
      if (!connection) {
        return rejectWithValue({
          message: requestsCopy.noConnection,
        });
      }

      let response;
      try {
        // Call stardog function
        response = await callback({
          ...args,
          connection: getStardogConnection(connection),
        });

        // Redirect to custom login page with expired response
        handleSessionTimeoutRedirectResponse(response);

        // Handle bad response
        if (isBody(response) && !response.ok) {
          throw new Error(response.statusText || String(response.status));
        }

        // Format response body
        if (
          response.body &&
          response.headers?.get('content-type') === MimeType.JSON_LD
        ) {
          response.body = await formatJsonLd(response.body);
        }

        return response;
      } catch (err) {
        if (connectionRefusedChecker.test((err as Error).message)) {
          // If connection is outright refused, we've lost (or never had) the
          // connection and need to set `isConnected` to `false` and bail.
          if (onConnectionRefused) {
            onConnectionRefused({ api, args, connection, err });
          }
        } else if (response && response.status === 401) {
          // Handle connections that are unauthorized by forcing the user to
          // connect again. This should only happen when an access token has expired.
          if (onConnectionUnauthorized) {
            onConnectionUnauthorized({ api, args, connection, err });
          }
        }

        return rejectWithValue({
          message: (err as Error).message || requestsCopy.operationFailed,
          response,
        });
      }
    });
